/**
 * Job Hunter — the candidate's truth file: the only facts any agent may use in
 * a cover note or a screening answer. Laid out on the CONTAINER width (the app
 * sidebar + page chrome leave a laptop far less room than the viewport says),
 * one card per topic. Form view and raw JSON view.
 */
import { ReactNode, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Braces, Briefcase, CalendarClock, GitBranch, Globe, GraduationCap, IdCard, Languages, MessageCircle, Plane, Plus, ShieldOff, Sparkles, Trash2, UserRound, Wallet,
} from 'lucide-react';
import { Button } from '@/components/Button';
import type { JhTruthFile } from '@/services/job-hunter.service';
import { Field, JhSelect, TagInput, ToggleRow, type JhOption } from '../ui';

const LEVELS: Array<JhOption<string>> = [
  { value: '', label: 'Not stated' },
  { value: 'A1', label: 'A1', description: 'Beginner' }, { value: 'A2', label: 'A2', description: 'Elementary' },
  { value: 'B1', label: 'B1', description: 'Intermediate' }, { value: 'B2', label: 'B2', description: 'Upper intermediate' },
  { value: 'C1', label: 'C1', description: 'Advanced' }, { value: 'C2', label: 'C2', description: 'Proficient' },
  { value: 'native', label: 'Native' },
];
const LANGS: Record<string, string> = {
  en: 'English', ur: 'Urdu', ar: 'Arabic', hi: 'Hindi', pa: 'Punjabi', de: 'German', fr: 'French', es: 'Spanish', it: 'Italian', nl: 'Dutch',
  pt: 'Portuguese', pl: 'Polish', tr: 'Turkish', fa: 'Persian', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ko: 'Korean', sv: 'Swedish',
  da: 'Danish', no: 'Norwegian', fi: 'Finnish', cs: 'Czech', el: 'Greek', ro: 'Romanian', hu: 'Hungarian', bn: 'Bengali', ms: 'Malay', id: 'Indonesian', tl: 'Filipino',
};
const CURRENCIES: Record<string, string> = {
  EUR: 'Euro', USD: 'US dollar', GBP: 'British pound', CAD: 'Canadian dollar', AUD: 'Australian dollar', NZD: 'New Zealand dollar',
  AED: 'UAE dirham', CHF: 'Swiss franc', SEK: 'Swedish krona', PLN: 'Polish złoty', PKR: 'Pakistani rupee', SAR: 'Saudi riyal',
};

function RemoveBtn({ onClick, label = 'Remove' }: { onClick: () => void; label?: string }) {
  return <button type="button" className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-border text-text-muted transition hover:border-danger hover:text-danger" onClick={onClick} aria-label={label} title={label}><Trash2 size={14} /></button>;
}

function Block({ icon, title, hint, children, className, action }: { icon: ReactNode; title: string; hint?: string; children: ReactNode; className?: string; action?: ReactNode }) {
  return (
    <section className={clsx('jh-card p-4', className)}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">{icon}</span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold tracking-tight">{title}</div>
            {hint && <div className="text-[11.5px] leading-snug text-text-muted">{hint}</div>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

const AddRow = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <button type="button" onClick={onClick} className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-dashed border-border-strong px-3 text-[12.5px] font-semibold text-text-secondary transition hover:border-primary hover:text-primary">
    <Plus size={13} />{children}
  </button>
);

export function TruthFileForm({ value, onChange, actions }: { value: JhTruthFile; onChange: (tf: JhTruthFile) => void; actions?: ReactNode }) {
  const [json, setJson] = useState<string | null>(null);
  const tf = value ?? {};
  const setTf = (patch: Partial<JhTruthFile>) => onChange({ ...tf, ...patch });
  const sub = <K extends keyof JhTruthFile>(k: K, patch: Record<string, unknown>) => setTf({ [k]: { ...((tf[k] as Record<string, unknown>) ?? {}), ...patch } } as Partial<JhTruthFile>);
  const langOptions = (code: string): Array<JhOption<string>> => [
    ...Object.entries(LANGS).map(([c, n]) => ({ value: c, label: n, description: c, keywords: c })),
    ...(code && !LANGS[code] ? [{ value: code, label: code.toUpperCase() }] : []),
  ];
  const currency = tf.compensation?.currency ?? 'EUR';

  return (
    <div className="@container">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="jh-muted max-w-2xl">The only facts an agent may use in a cover note or a screening answer. Missing facts become questions for you — never guesses.</p>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          <Button size="sm" variant="ghost" iconLeft={<Braces size={14} />} onClick={() => setJson(json === null ? JSON.stringify(tf, null, 2) : null)}>{json === null ? 'Edit as JSON' : 'Back to form'}</Button>
        </div>
      </div>

      {json !== null ? (
        <div className="jh-card p-4">
          <textarea className="jh-input font-mono text-[12px]" rows={22} value={json} onChange={(e) => setJson(e.target.value)} spellCheck={false} />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={() => {
              try {
                const parsed = JSON.parse(json);
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error();
                onChange(parsed); setJson(null); toast.success('Applied — save to keep it');
              } catch { toast.error('That is not a valid JSON object'); }
            }}>Apply JSON</Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 @5xl:grid-cols-2">
          <Block icon={<UserRound size={15} />} title="Identity" className="@5xl:col-span-2">
            <div className="grid gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
              <Field label="Full name"><input className="jh-input" value={tf.identity?.name ?? ''} onChange={(e) => sub('identity', { name: e.target.value })} /></Field>
              <Field label="Email"><input className="jh-input" type="email" value={tf.identity?.email ?? ''} onChange={(e) => sub('identity', { email: e.target.value })} /></Field>
              <Field label="Phone"><input className="jh-input" value={tf.identity?.phone ?? ''} onChange={(e) => sub('identity', { phone: e.target.value })} /></Field>
              <Field label="Current location"><input className="jh-input" value={tf.identity?.location ?? ''} onChange={(e) => sub('identity', { location: e.target.value })} placeholder="Dubai, UAE" /></Field>
            </div>
          </Block>

          <Block icon={<Plane size={15} />} title="Work rights" hint="Agents never claim a right to work the candidate doesn't have.">
            <div className="grid gap-3 @xl:grid-cols-2">
              <Field label="Nationality"><input className="jh-input" value={tf.work_rights?.nationality ?? ''} onChange={(e) => sub('work_rights', { nationality: e.target.value })} /></Field>
              <Field label="Current visa valid until"><input className="jh-input" type="month" value={tf.work_rights?.current_visa_valid_until ?? ''} onChange={(e) => sub('work_rights', { current_visa_valid_until: e.target.value })} /></Field>
            </div>
            <div className="mt-3 grid gap-2 @xl:grid-cols-2">
              <ToggleRow title="Needs visa sponsorship" checked={tf.work_rights?.needs_sponsorship !== false} onChange={(v) => sub('work_rights', { needs_sponsorship: v })} />
              <ToggleRow title="Has an EU work permit" checked={!!tf.work_rights?.eu_work_permit} onChange={(v) => sub('work_rights', { eu_work_permit: v })} />
            </div>
          </Block>

          <Block icon={<CalendarClock size={15} />} title="Availability">
            <div className="grid gap-3 @xl:grid-cols-2">
              <Field label="Notice period (weeks)"><input className="jh-input" type="number" min={0} value={tf.availability?.notice_period_weeks ?? ''} onChange={(e) => sub('availability', { notice_period_weeks: e.target.value === '' ? undefined : Number(e.target.value) })} /></Field>
              <Field label="Earliest relocation"><input className="jh-input" type="month" value={tf.availability?.earliest_relocation ?? ''} onChange={(e) => sub('availability', { earliest_relocation: e.target.value })} /></Field>
            </div>
            <div className="mt-3"><ToggleRow title="Open to a remote trial period" checked={!!tf.availability?.remote_trial_ok} onChange={(v) => sub('availability', { remote_trial_ok: v })} /></div>
          </Block>

          <Block icon={<Wallet size={15} />} title="Compensation" hint="Gross, per year. Used to answer salary questions.">
            <div className="grid gap-3 @xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <Field label="Currency">
                <JhSelect value={currency} onChange={(v) => sub('compensation', { currency: v })} searchPlaceholder="Search currency"
                  options={[...Object.entries(CURRENCIES).map(([c, n]) => ({ value: c, label: c, description: n, keywords: n })), ...(CURRENCIES[currency] ? [] : [{ value: currency, label: currency }])]} />
              </Field>
              <Field label="Minimum"><input className="jh-input" type="number" min={0} step={1000} value={tf.compensation?.min_annual || ''} onChange={(e) => sub('compensation', { min_annual: Number(e.target.value) || 0 })} placeholder="48000" /></Field>
              <Field label="Target"><input className="jh-input" type="number" min={0} step={1000} value={tf.compensation?.target_annual || ''} onChange={(e) => sub('compensation', { target_annual: Number(e.target.value) || 0 })} placeholder="60000" /></Field>
            </div>
          </Block>

          <Block icon={<Languages size={15} />} title="Languages">
            <div className="space-y-2">
              {(tf.languages ?? []).map((l, i) => (
                <div key={i} className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_40px] gap-2">
                  <JhSelect value={l.code} placeholder="Language" searchPlaceholder="Search language or code" options={langOptions(l.code)} searchable
                    onChange={(v) => setTf({ languages: (tf.languages ?? []).map((x, j) => (j === i ? { ...x, code: v } : x)) })} />
                  <JhSelect value={l.level ?? ''} options={LEVELS} searchable={false}
                    onChange={(v) => setTf({ languages: (tf.languages ?? []).map((x, j) => (j === i ? { ...x, level: v } : x)) })} />
                  <RemoveBtn onClick={() => setTf({ languages: (tf.languages ?? []).filter((_, j) => j !== i) })} />
                </div>
              ))}
              <AddRow onClick={() => setTf({ languages: [...(tf.languages ?? []), { code: '', level: '' }] })}>Add language</AddRow>
            </div>
          </Block>

          <Block icon={<GraduationCap size={15} />} title="Education">
            <div className="space-y-3">
              {(tf.education ?? []).map((ed, i) => (
                <div key={i} className="grid gap-2 rounded-xl border border-border bg-surface-alt/40 p-2.5 @2xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1.3fr)_120px_40px] @2xl:border-0 @2xl:bg-transparent @2xl:p-0">
                  <input className="jh-input" value={ed.degree} placeholder="Degree" aria-label="Degree" onChange={(e) => setTf({ education: (tf.education ?? []).map((x, j) => (j === i ? { ...x, degree: e.target.value } : x)) })} />
                  <input className="jh-input" value={ed.school} placeholder="School / university" aria-label="School" onChange={(e) => setTf({ education: (tf.education ?? []).map((x, j) => (j === i ? { ...x, school: e.target.value } : x)) })} />
                  <div className="grid grid-cols-[minmax(0,1fr)_40px] gap-2 @2xl:contents">
                    <input className="jh-input" value={ed.years} placeholder="2017–2021" aria-label="Years" onChange={(e) => setTf({ education: (tf.education ?? []).map((x, j) => (j === i ? { ...x, years: e.target.value } : x)) })} />
                    <RemoveBtn onClick={() => setTf({ education: (tf.education ?? []).filter((_, j) => j !== i) })} />
                  </div>
                </div>
              ))}
              <AddRow onClick={() => setTf({ education: [...(tf.education ?? []), { degree: '', school: '', years: '' }] })}>Add education</AddRow>
            </div>
          </Block>

          <Block icon={<Briefcase size={15} />} title="Years of experience" hint="Total, and per technology where it matters.">
            <div className="space-y-2">
              {Object.entries(tf.experience_years ?? { total: 0 }).map(([k, v]) => (
                <div key={k} className="grid grid-cols-[minmax(0,1fr)_96px_40px] gap-2">
                  {k === 'total'
                    ? <div className="flex h-10 items-center rounded-[10px] bg-surface-alt px-3 text-[13px] font-semibold">Total</div>
                    : <input className="jh-input" value={k} aria-label="Technology" onChange={(e) => { const next = { ...(tf.experience_years ?? {}) }; delete next[k]; next[e.target.value.toLowerCase()] = v; setTf({ experience_years: next }); }} />}
                  <input className="jh-input text-right tabular-nums" type="number" min={0} step={0.5} value={v} aria-label="Years" onChange={(e) => setTf({ experience_years: { ...(tf.experience_years ?? {}), [k]: Number(e.target.value) || 0 } })} />
                  {k !== 'total' ? <RemoveBtn onClick={() => { const next = { ...(tf.experience_years ?? {}) }; delete next[k]; setTf({ experience_years: next }); }} /> : <span />}
                </div>
              ))}
              <AddRow onClick={() => { const next = { ...(tf.experience_years ?? {}) }; let n = 1; while (next[`skill${n}`] !== undefined) n++; next[`skill${n}`] = 0; setTf({ experience_years: next }); }}>Add technology</AddRow>
            </div>
          </Block>

          <Block icon={<Sparkles size={15} />} title="Skills" hint="Press Enter after each one.">
            <TagInput value={tf.skills ?? []} onChange={(v) => setTf({ skills: v })} placeholder="Flutter, NestJS, MongoDB…" />
          </Block>

          <Block icon={<Globe size={15} />} title="Links">
            <div className="grid gap-2">
              {([['portfolio', 'Portfolio / website', Globe, 'https://…'], ['github', 'GitHub', GitBranch, 'https://github.com/…'], ['linkedin', 'LinkedIn', IdCard, 'https://linkedin.com/in/…']] as const).map(([key, label, Icon, ph]) => (
                <label key={key} className="flex items-center gap-2">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] border border-border bg-surface-alt text-text-muted" title={label}><Icon size={15} /></span>
                  <input className="jh-input" aria-label={label} value={tf.links?.[key] ?? ''} onChange={(e) => sub('links', { [key]: e.target.value })} placeholder={ph} />
                </label>
              ))}
            </div>
          </Block>

          <Block icon={<MessageCircle size={15} />} title="Saved answers" hint="Answers given on WhatsApp or in an application, reused next time." className="@5xl:col-span-2">
            <div className="space-y-2">
              {(tf.saved_answers ?? []).map((a, i) => (
                <div key={i} className="grid gap-2 rounded-xl border border-border bg-surface-alt/40 p-2.5 @2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px] @2xl:border-0 @2xl:bg-transparent @2xl:p-0">
                  <input className="jh-input" value={a.question} placeholder="Question" onChange={(e) => setTf({ saved_answers: (tf.saved_answers ?? []).map((x, j) => (j === i ? { ...x, question: e.target.value } : x)) })} />
                  <div className="grid grid-cols-[minmax(0,1fr)_40px] gap-2 @2xl:contents">
                    <input className="jh-input" value={a.answer} placeholder="Answer" onChange={(e) => setTf({ saved_answers: (tf.saved_answers ?? []).map((x, j) => (j === i ? { ...x, answer: e.target.value } : x)) })} />
                    <RemoveBtn onClick={() => setTf({ saved_answers: (tf.saved_answers ?? []).filter((_, j) => j !== i) })} />
                  </div>
                </div>
              ))}
              <AddRow onClick={() => setTf({ saved_answers: [...(tf.saved_answers ?? []), { question: '', answer: '' }] })}>Add answer</AddRow>
            </div>
          </Block>

          <Block icon={<ShieldOff size={15} />} title="Never answer" hint="The agents refuse these outright, whatever a form asks." className="@5xl:col-span-2">
            <TagInput value={tf.never_answer ?? []} onChange={(v) => setTf({ never_answer: v })} />
          </Block>
        </div>
      )}
    </div>
  );
}
